import React, { useState, useRef, useEffect } from 'react';
import { useLocation, useParams, useNavigate } from 'react-router-dom';
import { Wine, ChatCircle, PaperPlaneTilt, X, Star, Check } from '@phosphor-icons/react';
import {
  searchClient,
  ALGOLIA_APP_ID,
  ALGOLIA_SEARCH_API_KEY,
} from '../../services/algoliaClient';

// Algolia Chat Agent Configuration
// IMPORTANT: Verify these values in your Algolia Agent Studio dashboard:
// 1. Agent ID: Check in Agent Studio > Your Agent > Settings
// 2. API Key: REACT_APP_ALGOLIA_SEARCH_API_KEY — Search-Only with Agent Studio permissions
// 3. Agent Status: Must be Published/Active
const AGENT_ID = "c514d7e7-6868-4396-b178-e50f027fb116";

// No need for separate chat client - we'll use fetch directly

const LANGUAGES = {
  fr: { code: 'fr', name: 'Français', flag: '🇫🇷' },
  en: { code: 'en', name: 'English', flag: '🇬🇧' },
  es: { code: 'es', name: 'Español', flag: '🇪🇸' }
};

// Context-aware welcome messages
const getWelcomeMessage = (context, language) => {
  const messages = {
    fr: {
      product: (productName) => `Bonjour ! Je vois que vous consultez ${productName}. Je peux vous aider à trouver des accords mets-vins parfaits, suggérer des vins similaires, ou répondre à toutes vos questions sur ce produit.`,
      search: (query) => query 
        ? `Bonjour ! Vous recherchez "${query}". Je peux vous aider à affiner votre recherche, suggérer des alternatives, ou répondre à vos questions sur les vins.`
        : `Bonjour ! Je peux vous aider à trouver le vin parfait. Que recherchez-vous aujourd'hui ?`,
      category: (categoryName) => `Bonjour ! Vous explorez la catégorie ${categoryName}. Je peux vous guider dans cette catégorie, suggérer des produits, ou répondre à vos questions.`,
      country: (countryName) => `Bonjour ! Vous découvrez les vins de ${countryName}. Je peux vous parler des caractéristiques de cette région, suggérer des vins, ou répondre à vos questions.`,
      region: (regionName) => `Bonjour ! Vous explorez la région ${regionName}. Je peux vous guider sur les vins de cette région, leurs caractéristiques, ou répondre à vos questions.`,
      grape: (grapeName) => `Bonjour ! Vous vous intéressez au cépage ${grapeName}. Je peux vous parler de ce cépage, suggérer des vins, ou répondre à vos questions.`,
      default: `Bonjour ! Je suis votre assistant sommelier. Comment puis-je vous aider à trouver le vin parfait aujourd'hui ? Je peux vous proposer des recommandations personnalisées basées sur vos préférences.`
    },
    en: {
      product: (productName) => `Hello! I see you're viewing ${productName}. I can help you find perfect food pairings, suggest similar wines, or answer any questions about this product.`,
      search: (query) => query 
        ? `Hello! You're searching for "${query}". I can help you refine your search, suggest alternatives, or answer your wine questions.`
        : `Hello! I can help you find the perfect wine. What are you looking for today?`,
      category: (categoryName) => `Hello! You're exploring the ${categoryName} category. I can guide you through this category, suggest products, or answer your questions.`,
      country: (countryName) => `Hello! You're discovering wines from ${countryName}. I can tell you about this region's characteristics, suggest wines, or answer your questions.`,
      region: (regionName) => `Hello! You're exploring the ${regionName} region. I can guide you on wines from this region, their characteristics, or answer your questions.`,
      grape: (grapeName) => `Hello! You're interested in the ${grapeName} grape variety. I can tell you about this variety, suggest wines, or answer your questions.`,
      default: `Hello! I'm your sommelier assistant. How can I help you find the perfect wine today? I can provide personalized recommendations based on your preferences.`
    },
    es: {
      product: (productName) => `¡Hola! Veo que estás viendo ${productName}. Puedo ayudarte a encontrar maridajes perfectos, sugerir vinos similares o responder cualquier pregunta sobre este producto.`,
      search: (query) => query 
        ? `¡Hola! Estás buscando "${query}". Puedo ayudarte a refinar tu búsqueda, sugerir alternativas o responder tus preguntas sobre vinos.`
        : `¡Hola! Puedo ayudarte a encontrar el vino perfecto. ¿Qué estás buscando hoy?`,
      category: (categoryName) => `¡Hola! Estás explorando la categoría ${categoryName}. Puedo guiarte en esta categoría, sugerir productos o responder tus preguntas.`,
      country: (countryName) => `¡Hola! Estás descubriendo vinos de ${countryName}. Puedo contarte sobre las características de esta región, sugerir vinos o responder tus preguntas.`,
      region: (regionName) => `¡Hola! Estás explorando la región ${regionName}. Puedo guiarte sobre los vinos de esta región, sus características o responder tus preguntas.`,
      grape: (grapeName) => `¡Hola! Te interesa la variedad de uva ${grapeName}. Puedo contarte sobre esta variedad, sugerir vinos o responder tus preguntas.`,
      default: `¡Hola! Soy tu asistente sommelier. ¿Cómo puedo ayudarte a encontrar el vino perfecto hoy? Puedo proporcionar recomendaciones personalizadas basadas en tus preferencias.`
    }
  };

  const langMessages = messages[language] || messages.fr;
  
  if (context.type === 'product' && context.productName) {
    return langMessages.product(context.productName);
  } else if (context.type === 'search') {
    return langMessages.search(context.query);
  } else if (context.type === 'category' && context.categoryName) {
    return langMessages.category(context.categoryName);
  } else if (context.type === 'country' && context.countryName) {
    return langMessages.country(context.countryName);
  } else if (context.type === 'region' && context.regionName) {
    return langMessages.region(context.regionName);
  } else if (context.type === 'grape' && context.grapeName) {
    return langMessages.grape(context.grapeName);
  }
  
  return langMessages.default;
};

// Product Card Component for displaying search results
const ProductCard = ({ product, onNavigate }) => {
  const imageUrl = product.image 
    ? (product.image.startsWith('http') ? product.image : `https:${product.image}`)
    : null;
  const price = product.price || 0;
  const rating = product.attributes?.reviews_average_rating;
  const color = product.color || '';
  const country = product.attributes?.pays_origine || '';

  return (
    <div
      onClick={() => onNavigate(`/produit/${product.objectID}`)}
      style={{
        background: '#fff',
        borderRadius: 12,
        padding: 12,
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        border: '1px solid #e0e0e0',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        minWidth: 140,
        maxWidth: 180
      }}
      onMouseOver={(e) => {
        e.currentTarget.style.transform = 'translateY(-2px)';
        e.currentTarget.style.boxShadow = '0 4px 12px rgba(75,22,76,0.15)';
        e.currentTarget.style.borderColor = '#4B164C';
      }}
      onMouseOut={(e) => {
        e.currentTarget.style.transform = 'translateY(0)';
        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
        e.currentTarget.style.borderColor = '#e0e0e0';
      }}
    >
      {/* Product Image */}
      <div style={{
        width: '100%',
        height: 120,
        borderRadius: 8,
        background: '#f8f6fa',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'hidden'
      }}>
        {imageUrl ? (
          <img 
            src={imageUrl} 
            alt={product.name} 
            style={{ 
              width: '100%', 
              height: '100%', 
              objectFit: 'cover' 
            }} 
          />
        ) : (
          <Wine size={32} color="#4B164C" />
        )}
      </div>
      
      {/* Product Info */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <h4 style={{
          fontSize: 13,
          fontWeight: 600,
          color: '#333',
          margin: 0,
          lineHeight: 1.3,
          display: '-webkit-box',
          WebkitLineClamp: 2,
          WebkitBoxOrient: 'vertical',
          overflow: 'hidden'
        }}>
          {product.name}
        </h4>
        
        {(color || country) && (
          <div style={{ fontSize: 11, color: '#888' }}>
            {color}{color && country ? ' • ' : ''}{country}
          </div>
        )}
        
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginTop: 4
        }}>
          <span style={{ fontSize: 16, fontWeight: 700, color: '#4B164C' }}>
            ${price.toLocaleString('fr-CA', { minimumFractionDigits: 2 })}
          </span>
          {rating && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Star size={12} weight="fill" color="#4B164C" />
              <span style={{ fontSize: 11, color: '#4B164C', fontWeight: 600 }}>
                {rating}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const SommelierAssistant = () => {
  const location = useLocation();
  const params = useParams();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [language, setLanguage] = useState('fr');
  const [context, setContext] = useState({ type: 'default' });
  const [productData, setProductData] = useState(null);
  const [includeProductContext, setIncludeProductContext] = useState(true); // Auto-enable when on product page
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const isInitializedRef = useRef(false);
  const previousLanguageRef = useRef(language);
  const previousContextRef = useRef(context);
  const requestInProgressRef = useRef(false);
  const messagesRef = useRef([]);
  const productDataRef = useRef(null);
  const contextRef = useRef({ type: 'default' });
  const navigate = useNavigate();
  
  // Keep refs in sync with state
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  
  useEffect(() => {
    productDataRef.current = productData;
  }, [productData]);
  
  useEffect(() => {
    contextRef.current = context;
  }, [context]);

  // Detect page context
  useEffect(() => {
    const detectContext = async () => {
      const path = location.pathname;
      const searchParams = new URLSearchParams(location.search);
      
      // Extract productId from pathname since component is outside Routes
      const productIdMatch = path.match(/^\/produit\/([^\/]+)/);
      const productId = productIdMatch ? productIdMatch[1] : null;
      
      // Product page
      if (path.startsWith('/produit/') && productId) {
        try {
          const response = await searchClient.search([{
            indexName: 'saq_products',
            params: {
              filters: `objectID:${productId}`,
              hitsPerPage: 1
            }
          }]);
          
          if (response.results[0].hits.length > 0) {
            const product = response.results[0].hits[0];
            setProductData(product);
            setIncludeProductContext(true); // Auto-enable when product is detected
            const newContext = {
              type: 'product',
              productName: product.name || 'ce produit'
            };
            // Only update if context actually changed
            setContext(prev => {
              if (prev.type !== newContext.type || prev.productName !== newContext.productName) {
                return newContext;
              }
              return prev;
            });
          } else {
            console.warn('No product found for productId:', productId);
          }
        } catch (error) {
          console.error('Error fetching product:', error);
        }
      }
      // Search page
      else if (path === '/search') {
        const query = searchParams.get('query') || '';
        setContext(prev => {
          if (prev.type !== 'search' || prev.query !== query) {
            return { type: 'search', query };
          }
          return prev;
        });
      }
      // Category page
      else if (path.startsWith('/categories/')) {
        const category = params.category || params.subcategory || '';
        setContext(prev => {
          if (prev.type !== 'category' || prev.categoryName !== category) {
            return { type: 'category', categoryName: category };
          }
          return prev;
        });
      }
      // Country page
      else if (path.startsWith('/info/countries/') && params.country) {
        setContext(prev => {
          if (prev.type !== 'country' || prev.countryName !== params.country) {
            return { type: 'country', countryName: params.country };
          }
          return prev;
        });
      }
      // Region page
      else if (path.includes('/regions/') && params.region) {
        setContext(prev => {
          if (prev.type !== 'region' || prev.regionName !== params.region) {
            return { type: 'region', regionName: params.region };
          }
          return prev;
        });
      }
      // Grape variety page
      else if (path.includes('/cepages/') && params.grapeVariety) {
        setContext(prev => {
          if (prev.type !== 'grape' || prev.grapeName !== params.grapeVariety) {
            return { type: 'grape', grapeName: params.grapeVariety };
          }
          return prev;
        });
      }
      else {
        setContext(prev => {
          if (prev.type !== 'default') {
            setIncludeProductContext(false); // Disable when leaving product page
            return { type: 'default' };
          }
          return prev;
        });
      }
    };

    detectContext();
  }, [location.pathname, location.search]);

  // Initialize messages only when panel first opens
  useEffect(() => {
    if (isOpen && !isInitializedRef.current) {
      const welcomeMessage = getWelcomeMessage(context, language);
      setMessages([{
        id: 'welcome',
        role: 'assistant',
        content: welcomeMessage,
        timestamp: new Date()
      }]);
      isInitializedRef.current = true;
      previousLanguageRef.current = language;
      previousContextRef.current = context;
    } else if (!isOpen) {
      isInitializedRef.current = false;
    }
  }, [isOpen, context, language]);

  // Update welcome message when language or context changes (preserve existing messages)
  useEffect(() => {
    if (isOpen && isInitializedRef.current) {
      const languageChanged = previousLanguageRef.current !== language;
      const contextChanged = JSON.stringify(previousContextRef.current) !== JSON.stringify(context);
      
      if (languageChanged || contextChanged) {
        const welcomeMessage = getWelcomeMessage(context, language);
        // Update only the welcome message, keep other messages
        setMessages(prev => {
          const welcomeIndex = prev.findIndex(m => m.id === 'welcome');
          if (welcomeIndex !== -1) {
            const updated = [...prev];
            updated[welcomeIndex] = {
              ...updated[welcomeIndex],
              content: welcomeMessage
            };
            return updated;
          }
          return prev;
        });
        previousLanguageRef.current = language;
        previousContextRef.current = context;
      }
    }
  }, [isOpen, language, context]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const sendMessage = async () => {
    if (!inputValue.trim() || isLoading || requestInProgressRef.current) return;

    // Set flag immediately to prevent duplicate calls
    requestInProgressRef.current = true;
    setIsLoading(true);

    const userMessage = {
      id: `user-${Date.now()}`,
      role: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    const userInput = inputValue.trim();
    setInputValue('');

    // Add user message to state
    setMessages(prev => [...prev, userMessage]);

    // Get current messages from ref (which is kept in sync) and add user message
    const currentMessages = messagesRef.current || [];
    const allMessages = [...currentMessages, userMessage];

    // Build conversation history for context
    let conversationMessages = allMessages
      .filter(msg => msg && msg.id !== 'welcome' && !msg.isError)
      .map((msg, index, array) => {
        // Enhance the last user message with product context if enabled
        const isLastUserMessage = msg.role === 'user' && index === array.length - 1;
        
        // Extract productId from pathname
        const productIdMatch = location.pathname.match(/^\/produit\/([^\/]+)/);
        const productId = productIdMatch ? productIdMatch[1] : null;
        const isOnProductPage = location.pathname.startsWith('/produit/') && productId;
        
        // Check if product context should be included
        const shouldAddProductContext = isLastUserMessage && 
          includeProductContext && 
          isOnProductPage && 
          productData;
        
        if (shouldAddProductContext) {
          // Build product context
          const productInfo = [
            `Product Name: ${productData.name || 'Unknown'}`,
            productData.color ? `Color: ${productData.color}` : '',
            productData.attributes?.pays_origine ? `Country: ${productData.attributes.pays_origine}` : '',
            productData.attributes?.region_origine ? `Region: ${productData.attributes.region_origine}` : '',
            productData.attributes?.appellation ? `Appellation: ${productData.attributes.appellation}` : '',
            productData.attributes?.cepage ? `Grape Varieties: ${Array.isArray(productData.attributes.cepage) ? productData.attributes.cepage.join(', ') : productData.attributes.cepage}` : '',
            productData.attributes?.millesime_produit ? `Vintage: ${productData.attributes.millesime_produit}` : '',
            productData.price ? `Price: ${productData.price} CAD` : '',
            productData.attributes?.pourcentage_alcool_par_volume ? `Alcohol: ${productData.attributes.pourcentage_alcool_par_volume}%` : ''
          ].filter(Boolean).join('\n');

          const description = productData.attributes?.argumentaire_vente_externe 
            ? productData.attributes.argumentaire_vente_externe.replace(/<[^>]*>/g, '').substring(0, 500)
            : '';

          // Enhance user message with product context
          const contextText = language === 'fr'
            ? `[Contexte: L'utilisateur consulte la page produit "${productData.name}". Informations du produit:\n\n${productInfo}${description ? '\n\nDescription: ' + description : ''}]\n\nQuestion de l'utilisateur: ${msg.content}`
            : language === 'en'
            ? `[Context: The user is viewing the product page for "${productData.name}". Product information:\n\n${productInfo}${description ? '\n\nDescription: ' + description : ''}]\n\nUser question: ${msg.content}`
            : `[Contexto: El usuario está viendo la página del producto "${productData.name}". Información del producto:\n\n${productInfo}${description ? '\n\nDescripción: ' + description : ''}]\n\nPregunta del usuario: ${msg.content}`;

          return {
            role: 'user',
            parts: [
              { text: contextText }
            ]
          };
        }

        return {
          role: msg.role === 'user' ? 'user' : 'assistant',
          parts: [
            { text: msg.content || '' }
          ]
        };
      });

    try {
      // Use Algolia Agent Studio API endpoint
      const response = await fetch(
        `https://${ALGOLIA_APP_ID}.algolia.net/agent-studio/1/agents/${AGENT_ID}/completions?stream=false&compatibilityMode=ai-sdk-5`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'X-Algolia-API-Key': ALGOLIA_SEARCH_API_KEY,
            'X-Algolia-Application-Id': ALGOLIA_APP_ID,
          },
          body: JSON.stringify({
            messages: conversationMessages
          }),
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API Error:', response.status, errorText);
        throw new Error(`API Error: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      
      // Handle Algolia Agent Studio response format
      let assistantContent = '';
      let products = [];
      
      // Check for direct parts array (new format)
      if (data.parts && Array.isArray(data.parts)) {
        // Extract text content
        const textPart = data.parts.find(part => part.type === 'text' && part.text);
        if (textPart) {
          assistantContent = textPart.text;
        }
        
        // Extract product hits from tool calls
        const toolCallPart = data.parts.find(part => 
          part.type === 'tool-algolia_search_index' && 
          part.output && 
          part.output.hits
        );
        if (toolCallPart && toolCallPart.output.hits) {
          // Limit to 5 products
          products = toolCallPart.output.hits.slice(0, 5);
        }
      }
      // Check for candidates format (alternative format)
      else if (data.candidates && data.candidates[0] && data.candidates[0].content) {
        const content = data.candidates[0].content;
        if (content.parts && Array.isArray(content.parts)) {
          const textPart = content.parts.find(part => part.type === 'text' && part.text);
          if (textPart) {
            assistantContent = textPart.text;
          } else if (content.parts[0] && content.parts[0].text) {
            assistantContent = content.parts[0].text;
          }
        } else if (typeof content === 'string') {
          assistantContent = content;
        }
      } 
      // Check for other common response formats
      else if (data.message) {
        assistantContent = data.message;
      } else if (data.answer) {
        assistantContent = data.answer;
      } else if (data.response) {
        assistantContent = data.response;
      } else if (typeof data === 'string') {
        assistantContent = data;
      }
      
      // If we still don't have content, show error message
      if (!assistantContent) {
        console.warn('Could not extract content from response:', data);
        assistantContent = language === 'fr' 
          ? 'Je suis désolé, je n\'ai pas pu traiter votre demande. Pouvez-vous reformuler ?'
          : language === 'en'
          ? 'I\'m sorry, I couldn\'t process your request. Could you rephrase?'
          : 'Lo siento, no pude procesar tu solicitud. ¿Podrías reformular?';
      }

      const assistantMessage = {
        id: `assistant-${Date.now()}`,
        role: 'assistant',
        content: assistantContent,
        products: products.length > 0 ? products : undefined,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessages = {
        fr: 'Je rencontre une difficulté technique. Veuillez réessayer dans un instant.',
        en: 'I\'m experiencing a technical difficulty. Please try again in a moment.',
        es: 'Estoy experimentando una dificultad técnica. Por favor, inténtalo de nuevo en un momento.'
      };
      const errorMessage = {
        id: `error-${Date.now()}`,
        role: 'assistant',
        content: errorMessages[language] || errorMessages.fr,
        timestamp: new Date(),
        isError: true
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
      requestInProgressRef.current = false;
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const handleLanguageChange = (newLanguage) => {
    setLanguage(newLanguage);
    // Update welcome message but preserve conversation history
    const welcomeMessage = getWelcomeMessage(context, newLanguage);
    setMessages(prev => {
      const welcomeIndex = prev.findIndex(m => m.id === 'welcome');
      if (welcomeIndex !== -1) {
        const updated = [...prev];
        updated[welcomeIndex] = {
          ...updated[welcomeIndex],
          content: welcomeMessage
        };
        return updated;
      }
      return prev;
    });
  };

  return (
    <>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      {/* Floating Button */}
      {!isOpen && (
        <button
          onClick={() => setIsOpen(true)}
          style={{
            position: 'fixed',
            bottom: 24,
            right: 24,
            width: 64,
            height: 64,
            borderRadius: '50%',
            background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
            border: 'none',
            boxShadow: '0 8px 24px rgba(75,22,76,0.4)',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000,
            transition: 'all 0.3s ease',
          }}
          onMouseOver={(e) => {
            e.currentTarget.style.transform = 'scale(1.1)';
            e.currentTarget.style.boxShadow = '0 12px 32px rgba(75,22,76,0.5)';
          }}
          onMouseOut={(e) => {
            e.currentTarget.style.transform = 'scale(1)';
            e.currentTarget.style.boxShadow = '0 8px 24px rgba(75,22,76,0.4)';
          }}
        >
          <Wine size={32} color="#fff" weight="fill" />
        </button>
      )}

      {/* Chat Panel */}
      {isOpen && (
        <div style={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          width: '420px',
          maxWidth: 'calc(100vw - 48px)',
          height: '600px',
          maxHeight: 'calc(100vh - 48px)',
          background: 'linear-gradient(135deg, #f8f6fa 0%, #f0e8f2 100%)',
          borderRadius: 24,
          boxShadow: '0 16px 48px rgba(75,22,76,0.3)',
          border: '1px solid rgba(75,22,76,0.1)',
          display: 'flex',
          flexDirection: 'column',
          zIndex: 1001,
          overflow: 'hidden'
        }}>
          {/* Header */}
          <div style={{
            background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
            padding: '20px 24px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 16
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flex: 1 }}>
              <div style={{
                background: 'rgba(255,255,255,0.2)',
                borderRadius: 12,
                padding: 8,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Wine size={24} color="#fff" weight="fill" />
              </div>
              <div>
                <h3 style={{
                  fontSize: 18,
                  color: '#fff',
                  fontWeight: 700,
                  margin: 0,
                  marginBottom: 4
                }}>
                  Assistant Sommelier
                </h3>
                <p style={{
                  fontSize: 12,
                  color: 'rgba(255,255,255,0.9)',
                  margin: 0
                }}>
                  Recommandations personnalisées
                </p>
              </div>
            </div>
            
            {/* Language Selector */}
            <div style={{
              display: 'flex',
              gap: 4,
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 8,
              padding: 4
            }}>
              {Object.values(LANGUAGES).map((lang) => (
                <button
                  key={lang.code}
                  onClick={() => handleLanguageChange(lang.code)}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: 'none',
                    background: language === lang.code ? 'rgba(255,255,255,0.3)' : 'transparent',
                    color: '#fff',
                    fontSize: 12,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 4
                  }}
                  onMouseOver={(e) => {
                    if (language !== lang.code) {
                      e.currentTarget.style.background = 'rgba(255,255,255,0.2)';
                    }
                  }}
                  onMouseOut={(e) => {
                    if (language !== lang.code) {
                      e.currentTarget.style.background = 'transparent';
                    }
                  }}
                >
                  <span>{lang.flag}</span>
                </button>
              ))}
            </div>

            <button
              onClick={() => setIsOpen(false)}
              style={{
                background: 'rgba(255,255,255,0.15)',
                border: 'none',
                borderRadius: 8,
                width: 32,
                height: 32,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseOver={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.25)';
              }}
              onMouseOut={(e) => {
                e.currentTarget.style.background = 'rgba(255,255,255,0.15)';
              }}
            >
              <X size={18} color="#fff" />
            </button>
          </div>

          {/* Custom Chat UI */}
          <div
            ref={chatContainerRef}
            style={{
              flex: 1,
              overflow: 'hidden',
              display: 'flex',
              flexDirection: 'column',
              background: '#fff'
            }}
          >
            {/* Product Context Selector - Show when on product page */}
            {(() => {
              const productIdMatch = location.pathname.match(/^\/produit\/([^\/]+)/);
              const productId = productIdMatch ? productIdMatch[1] : null;
              const isOnProductPage = location.pathname.startsWith('/produit/') && productId;
              const shouldShow = isOnProductPage && (productData || context.type === 'product');
              
              if (!shouldShow) return null;
              
              return (
                <div style={{
                  padding: '12px 20px',
                  background: 'linear-gradient(135deg, #f8f6fa 0%, #f0e8f2 100%)',
                  borderBottom: '1px solid rgba(75,22,76,0.1)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 12
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flex: 1, minWidth: 0 }}>
                    <Wine size={16} color="#4B164C" weight="fill" />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: '#4B164C',
                        marginBottom: 2
                      }}>
                        {language === 'fr' ? 'Contexte produit:' :
                         language === 'en' ? 'Product context:' :
                         'Contexto del producto:'}
                      </div>
                      <div style={{
                        fontSize: 12,
                        color: '#666',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {productData?.name || context.productName || productId || 'Chargement...'}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={() => setIncludeProductContext(!includeProductContext)}
                    style={{
                      background: includeProductContext 
                        ? 'linear-gradient(135deg, #4B164C, #6B2C6B)' 
                        : '#e0e0e0',
                      border: 'none',
                      borderRadius: 8,
                      padding: '6px 12px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 6,
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      fontSize: 11,
                      fontWeight: 600,
                      color: includeProductContext ? '#fff' : '#666'
                    }}
                    onMouseOver={(e) => {
                      e.currentTarget.style.transform = 'scale(1.05)';
                    }}
                    onMouseOut={(e) => {
                      e.currentTarget.style.transform = 'scale(1)';
                    }}
                  >
                    {includeProductContext && <Check size={14} weight="bold" />}
                    <span>{includeProductContext 
                      ? (language === 'fr' ? 'Activé' : language === 'en' ? 'On' : 'Activado')
                      : (language === 'fr' ? 'Désactivé' : language === 'en' ? 'Off' : 'Desactivado')}</span>
                  </button>
                </div>
              );
            })()}

            {/* Messages Area */}
            <div
              style={{
                flex: 1,
                overflowY: 'auto',
                padding: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
                background: '#fff'
              }}
            >
              {messages.map((message) => (
                <div
                  key={message.id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
                    width: '100%'
                  }}
                >
                  {message.role === 'assistant' && (
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <Wine size={18} color="#fff" weight="fill" />
                    </div>
                  )}
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: message.products && message.products.length > 0 ? 16 : 0,
                      maxWidth: '80%'
                    }}
                  >
                    {/* Text Content - Show condensed version if products exist */}
                    {(() => {
                      const hasProducts = message.products && message.products.length > 0;
                      let displayContent = message.content;
                      let showFullText = true;
                      
                      // If products exist, show only intro/context, not the full list
                      if (hasProducts && message.content) {
                        // Look for patterns that indicate a list (numbered items, bullet points, etc.)
                        const hasListPattern = /\n\d+\.\s|\n•\s|\n-\s/.test(message.content);
                        
                        if (hasListPattern) {
                          // Extract just the intro paragraph(s) before the list starts
                          const paragraphs = message.content.split(/\n\n+/);
                          const introParagraphs = [];
                          
                          for (const para of paragraphs) {
                            // Stop when we hit a numbered list or bullet list
                            if (/\n\d+\.\s|\n•\s|\n-\s/.test(para)) {
                              break;
                            }
                            introParagraphs.push(para);
                          }
                          
                          if (introParagraphs.length > 0) {
                            displayContent = introParagraphs.join('\n\n');
                            showFullText = false;
                          } else {
                            // Fallback: get first 2-3 sentences
                            const sentences = message.content.match(/[^.!?]+[.!?]+/g) || [];
                            if (sentences.length > 0) {
                              displayContent = sentences.slice(0, 2).join(' ');
                              showFullText = false;
                            }
                          }
                        }
                      }
                      
                      return (
                        <div
                          style={{
                            background: message.role === 'user'
                              ? 'linear-gradient(135deg, #4B164C, #6B2C6B)'
                              : message.isError
                              ? 'linear-gradient(135deg, #fee, #fdd)'
                              : '#f8f6fa',
                            color: message.role === 'user' ? '#fff' : message.isError ? '#c33' : '#333',
                            padding: '12px 16px',
                            borderRadius: message.role === 'user'
                              ? '16px 16px 4px 16px'
                              : '16px 16px 16px 4px',
                            boxShadow: message.role === 'user'
                              ? '0 2px 8px rgba(75,22,76,0.2)'
                              : message.isError
                              ? '0 2px 8px rgba(204,51,51,0.1)'
                              : '0 1px 4px rgba(0,0,0,0.1)',
                            fontSize: 14,
                            lineHeight: 1.5,
                            whiteSpace: 'pre-wrap',
                            wordWrap: 'break-word',
                            border: message.isError ? '1px solid #fcc' : 'none'
                          }}
                        >
                          {displayContent}
                          {hasProducts && !showFullText && (
                            <div style={{ 
                              marginTop: 8, 
                              fontSize: 12, 
                              fontStyle: 'italic',
                              opacity: 0.7 
                            }}>
                              {language === 'fr' ? '→ Voir les produits recommandés ci-dessous' :
                               language === 'en' ? '→ See recommended products below' :
                               '→ Ver productos recomendados a continuación'}
                            </div>
                          )}
                        </div>
                      );
                    })()}
                    
                    {/* Product Cards - Show prominently when available */}
                    {message.products && message.products.length > 0 && (
                      <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8
                      }}>
                        <div style={{
                          fontSize: 12,
                          fontWeight: 600,
                          color: '#4B164C',
                          paddingLeft: 4
                        }}>
                          {language === 'fr' ? 'Produits recommandés :' :
                           language === 'en' ? 'Recommended products:' :
                           'Productos recomendados:'}
                        </div>
                        <div style={{
                          display: 'flex',
                          gap: 12,
                          flexWrap: 'wrap',
                          paddingTop: 4
                        }}>
                          {message.products.map((product) => (
                            <ProductCard
                              key={product.objectID}
                              product={product}
                              onNavigate={navigate}
                            />
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  {message.role === 'user' && (
                    <div
                      style={{
                        background: 'linear-gradient(135deg, #6B2C6B, #8B4C8B)',
                        borderRadius: '50%',
                        width: 32,
                        height: 32,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0
                      }}
                    >
                      <ChatCircle size={18} color="#fff" weight="fill" />
                    </div>
                  )}
                </div>
              ))}
              {isLoading && (
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px',
                    justifyContent: 'flex-start'
                  }}
                >
                  <div
                    style={{
                      background: 'linear-gradient(135deg, #4B164C, #6B2C6B)',
                      borderRadius: '50%',
                      width: 32,
                      height: 32,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0
                    }}
                  >
                    <Wine size={18} color="#fff" weight="fill" />
                  </div>
                  <div
                    style={{
                      background: '#f8f6fa',
                      padding: '12px 16px',
                      borderRadius: '16px 16px 16px 4px',
                      boxShadow: '0 1px 4px rgba(0,0,0,0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8
                    }}
                  >
                    <div style={{ 
                      width: 18, 
                      height: 18, 
                      border: '2px solid rgba(75,22,76,0.2)', 
                      borderTop: '2px solid #4B164C', 
                      borderRadius: '50%',
                      animation: 'spin 0.8s linear infinite'
                    }} />
                    <span style={{ fontSize: 14, color: '#666' }}>
                      {language === 'fr' ? 'Réflexion en cours...' : language === 'en' ? 'Thinking...' : 'Pensando...'}
                    </span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div
              style={{
                borderTop: '1px solid rgba(75,22,76,0.1)',
                padding: '16px',
                background: '#fff',
                display: 'flex',
                gap: '12px',
                alignItems: 'flex-end',
                flexShrink: 0
              }}
            >
              <textarea
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder={
                  language === 'fr' ? 'Posez votre question...' :
                  language === 'en' ? 'Ask your question...' :
                  'Haz tu pregunta...'
                }
                disabled={isLoading}
                style={{
                  flex: 1,
                  padding: '10px 14px',
                  borderRadius: 12,
                  border: '2px solid rgba(75,22,76,0.2)',
                  fontSize: 14,
                  fontFamily: 'inherit',
                  resize: 'none',
                  minHeight: 40,
                  maxHeight: 100,
                  outline: 'none',
                  background: '#fafafa',
                  transition: 'all 0.2s ease',
                  color: '#333'
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#4B164C';
                  e.target.style.background = '#fff';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = 'rgba(75,22,76,0.2)';
                  e.target.style.background = '#fafafa';
                }}
              />
              <button
                onClick={sendMessage}
                disabled={!inputValue.trim() || isLoading}
                style={{
                  background: isLoading || !inputValue.trim()
                    ? '#ccc'
                    : 'linear-gradient(135deg, #4B164C, #6B2C6B)',
                  border: 'none',
                  borderRadius: 12,
                  width: 40,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: isLoading || !inputValue.trim() ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  boxShadow: isLoading || !inputValue.trim()
                    ? 'none'
                    : '0 2px 8px rgba(75,22,76,0.3)',
                  flexShrink: 0
                }}
                onMouseOver={(e) => {
                  if (!isLoading && inputValue.trim()) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(75,22,76,0.4)';
                  }
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = isLoading || !inputValue.trim()
                    ? 'none'
                    : '0 2px 8px rgba(75,22,76,0.3)';
                }}
              >
                {isLoading ? (
                  <div style={{ 
                    width: 18, 
                    height: 18, 
                    border: '2px solid rgba(255,255,255,0.3)', 
                    borderTop: '2px solid #fff', 
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite'
                  }} />
                ) : (
                  <PaperPlaneTilt size={18} color="#fff" weight="fill" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
